import "./MatchStatus.css";

import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";

import {
  ISoccerMatch,
  SoccerMatchStatus,
  SoccerMatchStatusLabel,
} from "../../../../../../types";
import { useMatchResult, useNowTick } from "../../../../hooks";
import { liveClock } from "../../../../utils/liveClock";

export const MatchStatus = ({
  status,
  prematch = false,
  soccerMatch,
}: {
  status: SoccerMatchStatus;
  prematch?: boolean;
  soccerMatch?: ISoccerMatch;
}) => {
  const { matchInfoSummary } = useMatchResult({ soccerMatch: soccerMatch! });

  if (prematch) return <></>;

  const statusBadge = MatchStatusMapper[status]?.(soccerMatch) ?? <></>;
  const isEnded = status === SoccerMatchStatus.PLAYED;

  return (
    <div className="match-status-wrapper">
      {statusBadge}
      {isEnded && !!matchInfoSummary && (
        <span className="match-result-summary">{matchInfoSummary}</span>
      )}
    </div>
  );
};

const BeforeMatch = () => {
  return (
    <div className="match-status">
      {MatchStatusLabelMapper[SoccerMatchStatusLabel.BEFORE_MATCH]}
    </div>
  );
};

const PlayingMatch = ({ soccerMatch }: { soccerMatch?: ISoccerMatch }) => {
  // Subscribe to the cache slot fed by useSoccerMatch / useScheduleMatches.
  const { dataUpdatedAt } = useQuery({
    queryKey: [`fixture-${soccerMatch?.id}`],
    enabled: false,
  });
  const now = useNowTick();

  return (
    <div className="match-status live-game">
      <span className="match-status-live-dot" aria-hidden="true" />
      <span>{liveClock(soccerMatch, dataUpdatedAt, now)}</span>
    </div>
  );
};

const AfterMatchUI = () => {
  return (
    <div className="match-status post-game">
      {t("gameStatus.fullTime")}
    </div>
  );
};

const MatchStatusLabelMapper = {
  [SoccerMatchStatusLabel.BEFORE_MATCH]: t("matchStatus.beforeMatch"),
  [SoccerMatchStatusLabel.LIVE]: t("matchStatus.live"),
  [SoccerMatchStatusLabel.MATCH_OVER]: t("matchStatus.matchOver"),
};

const MatchStatusMapper: Partial<
  Record<SoccerMatchStatus, (soccerMatch?: ISoccerMatch) => JSX.Element>
> = {
  [SoccerMatchStatus.FIXTURE]: () => <BeforeMatch />,
  [SoccerMatchStatus.CANCELLED]: () => <BeforeMatch />,
  [SoccerMatchStatus.SUSPENDED]: () => <BeforeMatch />,
  [SoccerMatchStatus.POSTPONED]: () => <BeforeMatch />,
  [SoccerMatchStatus.PLAYING]: (soccerMatch) => (
    <PlayingMatch soccerMatch={soccerMatch} />
  ),
  [SoccerMatchStatus.PLAYED]: () => <AfterMatchUI />,
  [SoccerMatchStatus.AWARDED]: () => <BeforeMatch />,
};
